import { CSSProperties, forwardRef, useMemo } from 'react';
import SimpleBarCore from 'simplebar-core';
import type { Props as SimpleBarProps } from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';

import { StyledScrollArea } from './ScrollArea.style';

/**
 * ScrollArea component props interface.
 *
 * Note: This interface includes only commonly used SimpleBar props for simplicity.
 * SimpleBar offers additional configuration options like forceVisible, clickOnTrack,
 * scrollbarMinSize, scrollbarMaxSize, and custom classNames.
 *
 * For advanced use cases requiring additional props, refer to the SimpleBar documentation:
 * https://github.com/Grsmto/simplebar/tree/master/packages/simplebar-react
 */
export interface ScrollAreaProps {
  clickOnTrack?: boolean;
  autoHide?: boolean;
  children?: SimpleBarProps['children'];
  className?: string;
  id?: string;
  locked?: boolean;
  scrollableNodeClassName?: string;
  scrollableNodeProps?: SimpleBarProps['scrollableNodeProps'];
  style?: React.CSSProperties;
}

export const ScrollArea = forwardRef<SimpleBarCore | null, ScrollAreaProps>(function ScrollArea(
  {
    clickOnTrack = true,
    autoHide = false,
    children,
    className,
    id,
    locked = false,
    scrollableNodeClassName,
    scrollableNodeProps,
    style,
  },
  ref
) {
  const mergedScrollableNodeProps = useMemo(() => {
    const providedClassName = scrollableNodeProps?.className;
    const providedStyle = scrollableNodeProps?.style as CSSProperties | undefined;

    const style: CSSProperties = {
      scrollbarGutter: 'stable both-edges',
      ...providedStyle,
    };

    if (locked) {
      style.overflowY = 'hidden';
    } else if (!providedStyle || providedStyle.overflowY === undefined) {
      style.overflowY = 'auto';
    }

    return {
      ...scrollableNodeProps,
      className:
        [scrollableNodeClassName, providedClassName].filter(Boolean).join(' ') || undefined,
      style,
    };
  }, [locked, scrollableNodeClassName, scrollableNodeProps]);

  return (
    <StyledScrollArea
      ref={ref}
      autoHide={autoHide}
      className={className}
      id={id}
      style={style}
      scrollableNodeProps={mergedScrollableNodeProps}
      clickOnTrack={clickOnTrack}
    >
      {children}
    </StyledScrollArea>
  );
});
